import { Inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  Router,
} from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, of, timeout } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { environment } from '@environments/environment';
import { CommonService } from '../services';
import { CookieService } from 'ngx-cookie-service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Guid } from 'guid-typescript';
import {
  AuthCheckResult,
  AuthProviderId,
  AuthSessionService,
  isAccessTokenFor,
  readJwt,
} from '../auth';

interface SpAuthContext {
  userId?: string;
  cbCustomerId?: string;
  externalUserGuid?: string;
  themeColor?: string;
}

const AUTH_CONTEXT_CACHE_KEY = 'spAuthContextLoadedAt';
const AUTH_CONTEXT_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * One flow for both sign-in systems (OneLogin and Identity, see core/auth):
 * the library check picks the system, the token is checked for that system, then SP
 * `auth/context` (which resolves either token) fills the SP identity cookies.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {
  isLogin = false;
  appDomain = environment.appDomain;
  domain = 'capium.co.uk';
  uiUrl = environment.uiUrl;

  constructor(
    private router: Router,
    public authSession: AuthSessionService,
    public commonService: CommonService,
    @Inject(DOCUMENT) private document: Document,
    private cookieService: CookieService,
    private spinner: NgxSpinnerService,
    private http: HttpClient
  ) {}

  canActivateChild(
    next: ActivatedRouteSnapshot
  ): Observable<boolean> | boolean {
    return this.canActivate(next);
  }

  canActivate(_route: ActivatedRouteSnapshot): Observable<boolean> | boolean {
    const onOidcCallback = this.authSession.isOidcCallbackUrl();
    const userId = this.cookieService.get('userId') as unknown as Guid;
    if (userId === ('' as unknown as Guid) || onOidcCallback) {
      this.spinner.show();
    }

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('state') === 'succeeded') {
      this.router.navigate(['/home/subscriptions']);
      return this.completeAuthenticatedSession();
    }

    return this.authSession.checkAuth().pipe(
      switchMap((result: AuthCheckResult) => {
        this.isLogin = result.isAuthenticated;

        // OIDC callback: leave /?code=... immediately — never block on auth/context.
        if (onOidcCallback) {
          return this.finishOidcCallback(result.provider, result.accessToken);
        }

        return this.ensureSession(result.provider, result.accessToken);
      }),
      catchError((err) => {
        console.error('[Auth] checkAuth failed', err);
        this.spinner.hide();
        if (this.authSession.isOidcCallbackUrl()) {
          this.document.location.replace(
            this.document.location.origin + '/home/products'
          );
          return of(false);
        }
        this.router.navigate(['/home/products']);
        return of(false);
      }),
      finalize(() => {
        if (!this.authSession.isOidcCallbackUrl()) {
          this.spinner.hide();
        }
      })
    );
  }

  notLoggedIn(): void {
    const url = window.location.href;

    localStorage.clear();
    sessionStorage.clear();
    this.authSession.rememberAfterStorageClear();

    this.deleteAllCookies();

    if (url.split('/')[url.split('/').length - 1] === 'register') {
      this.isLogin = false;
    } else {
      this.login();
    }
  }

  login(): void {
    this.authSession.login();
  }

  logout(): void {
    this.authSession.logout(() => {
      this.deleteAllCookies();
      localStorage.clear();
      sessionStorage.clear();
    });
  }

  deleteAllCookies(): void {
    const cookies = document.cookie.split(';');

    for (const element of cookies) {
      const cookie = element;
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
      document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
    }
  }

  /** After authorize redirect: set cookies from token, then hard-navigate off callback URL. */
  private finishOidcCallback(
    provider: AuthProviderId,
    token: string | undefined
  ): Observable<boolean> {
    if (!token || !isAccessTokenFor(provider, token)) {
      console.error(`[Auth] OIDC callback without a ${provider} access_token`);
      this.spinner.hide();
      this.notLoggedIn();
      return of(false);
    }

    try {
      this.authSession.claimSpIdentityCookies(provider, token);
      this.applyTokenCookies(provider, token);
    } catch (e) {
      console.error('[Auth] Failed to decode access_token on callback', e);
      this.spinner.hide();
      this.notLoggedIn();
      return of(false);
    }

    // Force products AuthGuard to await /auth/context (X-User headers no longer bootstrap cookies).
    sessionStorage.removeItem(AUTH_CONTEXT_CACHE_KEY);

    // Fire-and-forget SP identity bootstrap; navigation must not wait.
    this.loadAuthContextInBackground();

    this.spinner.hide();
    this.document.location.replace(
      this.document.location.origin + '/home/products'
    );
    return of(false);
  }

  private completeAuthenticatedSession(): Observable<boolean> {
    return this.authSession
      .accessToken$()
      .pipe(
        switchMap((token) =>
          this.ensureSession(this.authSession.provider, token)
        )
      );
  }

  private ensureSession(
    provider: AuthProviderId,
    token: string | undefined
  ): Observable<boolean> {
    if (!token) {
      this.notLoggedIn();
      return of(false);
    }

    if (!isAccessTokenFor(provider, token)) {
      if (provider === 'identity') {
        // As the Identity-only build: a token without tenantName does not open the portal.
        console.error('[Auth] Identity access_token has no tenantName.');
        this.spinner.hide();
        return of(false);
      }
      console.error(
        '[Auth] Stored token is not a OneLogin access_token. Clearing and re-auth via OneLogin.'
      );
      this.notLoggedIn();
      return of(false);
    }

    try {
      this.authSession.claimSpIdentityCookies(provider, token);
      this.applyTokenCookies(provider, token);
    } catch (e) {
      console.error('[Auth] Failed to decode access_token', e);
      this.notLoggedIn();
      return of(false);
    }

    if (!this.shouldLoadAuthContext()) {
      this.spinner.hide();
      return of(true);
    }

    return this.http
      .get<SpAuthContext>(this.authContextUrl())
      .pipe(
        timeout(8000),
        map((ctx) => {
          this.applyAuthContextCookies(ctx);
          this.markAuthContextLoaded();
          this.spinner.hide();
          return true;
        }),
        catchError((err) => {
          console.error('[Auth] SP auth/context failed (continuing)', err);
          const oneLoginUserId = this.cookieService.get('oneLoginUserId');
          if (oneLoginUserId && !this.cookieService.get('userId')) {
            this.setCookie('userId', oneLoginUserId);
          }
          this.spinner.hide();
          return of(true);
        })
      );
  }

  /** `auth/context` resolves OneLogin, APIM and Identity callers alike; the host is the active system's. */
  private authContextUrl(): string {
    return `${this.authSession.settings.apiVersion2Url}auth/context`;
  }

  private applyTokenCookies(provider: AuthProviderId, token: string): void {
    const decoded = readJwt(token)?.payload;
    if (!decoded) {
      throw new Error('access_token is not a readable JWT');
    }

    if (provider === 'identity') {
      // Identity carries the SP identity in the token itself (as the Identity-only build read it).
      this.setCookie('isLoggedin', decoded['userId'] ? 'true' : 'false');
      if (decoded['userId']) {
        this.setCookie('userId', String(decoded['userId']));
      }
      const cbCustomerId =
        decoded['subcriptionClientId'] ?? decoded['chargebecustomerId'];
      if (cbCustomerId) {
        this.setCookie('customerId', String(cbCustomerId));
      }
      if (decoded['ExternalUserGUID']) {
        this.setCookie('externalUserGUID', String(decoded['ExternalUserGUID']));
      }
      return;
    }

    const oneLoginUserId = decoded['sub'];
    if (!oneLoginUserId) {
      throw new Error('access_token missing sub');
    }

    const firmId = decoded['firm_id'] ?? decoded['firmId'];
    if (!firmId) {
      console.warn(
        '[Auth] OneLogin token missing firm_id — SP APIM resolution may fail.',
        { aud: decoded['aud'], iss: decoded['iss'], sub: oneLoginUserId }
      );
    }

    this.setCookie('isLoggedin', 'true');
    this.setCookie('oneLoginUserId', String(oneLoginUserId));
  }

  private applyAuthContextCookies(ctx: SpAuthContext | null | undefined): void {
    if (!ctx) {
      return;
    }
    const userId = ctx.userId ?? (ctx as any).UserId;
    if (userId) {
      this.setCookie('userId', String(userId));
    }
    const cbCustomerId = ctx.cbCustomerId ?? (ctx as any).CbCustomerId;
    if (cbCustomerId) {
      this.setCookie('customerId', cbCustomerId);
    } else {
      const oneLoginUserId = this.cookieService.get('oneLoginUserId');
      const bad = this.cookieService.get('customerId');
      if (bad && oneLoginUserId && bad === oneLoginUserId) {
        this.cookieService.delete('customerId', '/');
      }
    }
    const externalUserGuid =
      ctx.externalUserGuid ?? (ctx as any).ExternalUserGuid;
    if (externalUserGuid) {
      this.setCookie('externalUserGUID', externalUserGuid);
    }
    const themeColor = ctx.themeColor ?? (ctx as any).ThemeColor;
    if (themeColor) {
      this.setCookie('themecolor', themeColor);
    }
  }

  private loadAuthContextInBackground(): void {
    if (!this.shouldLoadAuthContext()) {
      return;
    }

    this.http
      .get<SpAuthContext>(this.authContextUrl())
      .pipe(
        timeout(8000),
        catchError((err) => {
          console.error('[Auth] background auth/context failed', err);
          return of(null);
        })
      )
      .subscribe((ctx) => {
        if (ctx) {
          this.applyAuthContextCookies(ctx);
          this.markAuthContextLoaded();
        } else {
          const oneLoginUserId = this.cookieService.get('oneLoginUserId');
          if (oneLoginUserId && !this.cookieService.get('userId')) {
            this.setCookie('userId', oneLoginUserId);
          }
        }
      });
  }

  private shouldLoadAuthContext(): boolean {
    const hasUserId = !!this.cookieService.get('userId');
    const hasCustomerId = !!this.cookieService.get('customerId');
    const hasExternalGuid = !!this.cookieService.get('externalUserGUID');
    const cachedAt = Number(sessionStorage.getItem(AUTH_CONTEXT_CACHE_KEY) || 0);
    const now = Date.now();

    // After removing browser X-User-* headers, identity cookies only arrive via /auth/context.
    // Keep refreshing until cookies exist (new firms may never get a Chargebee id — throttle).
    if (!hasUserId || !hasCustomerId || !hasExternalGuid) {
      return !cachedAt || now - cachedAt > 30_000;
    }

    return !cachedAt || now - cachedAt > AUTH_CONTEXT_REFRESH_INTERVAL_MS;
  }

  private markAuthContextLoaded(): void {
    sessionStorage.setItem(AUTH_CONTEXT_CACHE_KEY, String(Date.now()));
  }

  private setCookie(name: string, value: string): void {
    this.cookieService.set(name, value, undefined, '/');
  }
}
